import type { MouseEvent } from 'react';
import type { Deck, DeckCategory } from '@/framework/types';
import { categoryMeta, categoryOrder, isDeckPublic } from '@/registry';
import { navigate } from '@/router';
import { SignOutButton } from '@/auth/SignOutButton';
import '@/framework/styles/home.css';

function formatDate(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
    .format(d)
    .toUpperCase();
}

function onDeckClick(e: MouseEvent<HTMLAnchorElement>, slug: string) {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  navigate(`/${slug}`);
}

/** Decks with a `date` sort newest first; undated decks sort last in insertion order. */
function sortDecksByDateDesc(a: Deck, b: Deck): number {
  if (a.date && b.date) return b.date.localeCompare(a.date);
  if (a.date) return -1;
  if (b.date) return 1;
  return 0;
}

function decksFor(category: DeckCategory, decks: readonly Deck[]): readonly Deck[] {
  return decks.filter(d => d.category === category).slice().sort(sortDecksByDateDesc);
}

export function Home({ decks }: { decks: readonly Deck[] }) {
  const sections = categoryOrder
    .map(category => ({ category, decks: decksFor(category, decks) }))
    .filter(s => s.decks.length > 0);

  return (
    <div className="home sn-section-beige">
      <header className="home-header">
        <img src="/assets/logomark-blue.svg" alt="" aria-hidden="true" />
        <h1>StreamNative Decks</h1>
        <span className="home-tag">
          {decks.length} deck{decks.length === 1 ? '' : 's'} · {sections.length} categor
          {sections.length === 1 ? 'y' : 'ies'}
        </span>
        <SignOutButton className="home-signout" />
      </header>

      <main className="home-events">
        {sections.map(({ category, decks: categoryDecks }) => {
          const meta = categoryMeta[category];
          return (
            <section key={category} className="event-card">
              <div className="event-meta">
                <span className="event-date">{meta.name}</span>
                <p className="event-organizers">{meta.blurb}</p>
              </div>
              <div className="event-content">
                <ul className="deck-list">
                  {categoryDecks.map(deck => {
                    const isPublic = isDeckPublic(deck);
                    return (
                      <li key={deck.slug}>
                        <a
                          href={`/${deck.slug}`}
                          className="deck-link"
                          data-visibility={isPublic ? 'public' : 'protected'}
                          onClick={e => onDeckClick(e, deck.slug)}
                        >
                          <span
                            className={`deck-visibility deck-visibility--${isPublic ? 'public' : 'protected'}`}
                          >
                            {isPublic ? 'Public' : 'Internal'}
                          </span>
                          <div className="deck-speaker">{deck.speaker}</div>
                          <div className="deck-title">{deck.title}</div>
                          {deck.description && (
                            <div className="deck-description">{deck.description}</div>
                          )}
                          {(deck.date || deck.context) && (
                            <div className="deck-context">
                              {deck.date && (
                                <span className="deck-date">{formatDate(deck.date)}</span>
                              )}
                              {deck.context && (
                                <span className="deck-context-text">{deck.context}</span>
                              )}
                            </div>
                          )}
                          <span className="deck-arrow" aria-hidden="true">
                            →
                          </span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </section>
          );
        })}
      </main>

      <footer className="home-footer">
        <span>streamnative.io</span>
        <span>Funnel Display · Funnel Sans · DM Mono · SN Design System v1</span>
      </footer>
    </div>
  );
}
